import {
  CATEGORY_ROWS,
  type CategoryRowKey,
  groupTagsByCategory,
} from '@/components/words/tag-editor';
import { tagsApi } from '@/lib/api';
import { useTweak } from '@/lib/feature-flags';
import type { WordTag } from '@/schemas/word';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface AnchorPoint {
  x: number;
  y: number;
}

interface TagCategoryPickerProps {
  /** All of the word's current tags — categories not touched this session pass through unchanged. */
  wordTags: WordTag[];
  /** Category to open on. Also the only category shown for the `modal`/`popover` styles. */
  initialCategory: CategoryRowKey;
  /** Anchor point (e.g. the `+` button's bottom-left corner) used by the `popover` style. */
  anchorPoint?: AnchorPoint | undefined;
  /** Override the `tag_picker_style` dev tweak for this instance (bulk-add always forces `tabbed`). */
  forceStyle?: 'modal' | 'popover' | 'tabbed' | undefined;
  onConfirm: (nextTags: WordTag[]) => void;
  onClose: () => void;
}

function emptySelection(): Record<CategoryRowKey, Set<string>> {
  const groups: Record<CategoryRowKey, Set<string>> = {
    type: new Set(),
    date: new Set(),
    geo: new Set(),
    metier: new Set(),
    langue: new Set(),
    none: new Set(),
  };
  return groups;
}

function seedSelection(wordTags: WordTag[]): Record<CategoryRowKey, Set<string>> {
  const groups = groupTagsByCategory(wordTags);
  const selection = emptySelection();
  for (const { key } of CATEGORY_ROWS) {
    for (const tag of groups[key]) selection[key].add(tag.label);
  }
  return selection;
}

/**
 * Per-category tag picker (Point 6): search + a scrollable grid of that
 * category's tags as toggleable chips (ordered by frequency desc), multi-select
 * with the current selection pre-checked. Tag creation is available in every
 * category, including "Non catégorisé" — the search field doubles as create.
 *
 * Three render styles, toggleable via the `tag_picker_style` dev tweak:
 * `modal` (default) — centered overlay; `popover` — panel anchored at
 * `anchorPoint`; `tabbed` — a single modal with all six categories as tabs,
 * so a session can touch more than one category before confirming.
 *
 * `onConfirm` always receives the FULL next tag array for the word (every
 * category's current selection, not just the one being edited), since a
 * `tabbed` session may have touched several.
 */
export function TagCategoryPicker({
  wordTags,
  initialCategory,
  anchorPoint,
  forceStyle,
  onConfirm,
  onClose,
}: TagCategoryPickerProps) {
  const queryClient = useQueryClient();
  const tweakStyle = useTweak('tag_picker_style');
  const style = forceStyle ?? tweakStyle;
  const tabbed = style === 'tabbed';
  const popover = style === 'popover';

  const [activeCategory, setActiveCategory] = useState<CategoryRowKey>(initialCategory);
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<Record<CategoryRowKey, Set<string>>>(() =>
    seedSelection(wordTags),
  );
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: catalog = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: tagsApi.list,
    staleTime: 10 * 60_000,
  });

  const catalogGroups = useMemo(() => groupTagsByCategory(catalog), [catalog]);

  const knownTagsByLabel = useMemo(() => {
    const map = new Map<string, WordTag>();
    for (const tag of wordTags) map.set(tag.label.toLowerCase(), tag);
    for (const tag of catalog) map.set(tag.label.toLowerCase(), tag);
    return map;
  }, [wordTags, catalog]);

  const activeLabel = CATEGORY_ROWS.find((c) => c.key === activeCategory)?.label ?? '';
  const trimmedSearch = search.trim();

  const options = useMemo(() => {
    const all = catalogGroups[activeCategory];
    const filtered = trimmedSearch
      ? all.filter((t) => t.label.toLowerCase().includes(trimmedSearch.toLowerCase()))
      : all;
    return [...filtered].sort((a, b) => (b.frequency ?? 0) - (a.frequency ?? 0));
  }, [catalogGroups, activeCategory, trimmedSearch]);

  const showCreate = trimmedSearch.length > 0 && !knownTagsByLabel.has(trimmedSearch.toLowerCase());

  function toggle(label: string) {
    setSelection((prev) => {
      const next = new Set(prev[activeCategory]);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return { ...prev, [activeCategory]: next };
    });
  }

  function handleCreate() {
    const label = trimmedSearch;
    if (!label) return;
    const category = activeCategory === 'none' ? null : activeCategory;
    const newTag: WordTag = { label, ...(category ? { tagCategory: category } : {}) };

    queryClient.setQueryData<WordTag[]>(['tags'], (prev = []) => [...prev, newTag]);
    tagsApi
      .create(label, category)
      .then(() => queryClient.invalidateQueries({ queryKey: ['tags'] }))
      .catch(() => {
        queryClient.setQueryData<WordTag[]>(['tags'], (prev = []) =>
          prev.filter((t) => t.label !== label),
        );
      });

    setSelection((prev) => ({
      ...prev,
      [activeCategory]: new Set(prev[activeCategory]).add(label),
    }));
    setSearch('');
    searchInputRef.current?.focus();
  }

  function handleTabSelect(category: CategoryRowKey) {
    setActiveCategory(category);
    setSearch('');
  }

  function handleConfirm() {
    const next: WordTag[] = [];
    for (const { key } of CATEGORY_ROWS) {
      for (const label of selection[key]) {
        const known = knownTagsByLabel.get(label.toLowerCase());
        next.push(known ?? { label, ...(key !== 'none' ? { tagCategory: key } : {}) });
      }
    }
    onConfirm(next);
  }

  const selectedCount = selection[activeCategory].size;

  const containerStyle = popover
    ? ({
        position: 'fixed',
        left: anchorPoint?.x ?? 16,
        top: anchorPoint?.y ?? 16,
      } as const)
    : undefined;

  const body = (
    <div className={`tag-picker${popover ? ' tag-picker--popover' : ''}`} style={containerStyle}>
      <div className="tag-picker__header">
        <span className="tag-picker__title">{tabbed ? 'Tags' : activeLabel}</span>
        <button type="button" className="tag-picker__close" aria-label="Fermer" onClick={onClose}>
          <span className="material-symbols-rounded">close</span>
        </button>
      </div>

      {tabbed && (
        <div className="tag-picker__tabs" role="tablist">
          {CATEGORY_ROWS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={key === activeCategory}
              className={`tag-picker__tab${key === activeCategory ? ' is-active' : ''}`}
              onClick={() => handleTabSelect(key)}
            >
              {label}
              {selection[key].size > 0 && (
                <span className="tag-picker__tab-count">{selection[key].size}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <input
        ref={searchInputRef}
        type="text"
        className="tag-picker__search"
        placeholder="Rechercher ou créer un tag…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && showCreate) {
            e.preventDefault();
            handleCreate();
          }
        }}
        aria-label={`Rechercher un tag « ${activeLabel} »`}
      />

      <div className="tag-picker__grid">
        {options.map((tag) => {
          const isSelected = selection[activeCategory].has(tag.label);
          return (
            <button
              key={tag.label}
              type="button"
              className={`tag-picker__chip${isSelected ? ' is-selected' : ''}`}
              aria-pressed={isSelected}
              onClick={() => toggle(tag.label)}
            >
              {tag.label}
            </button>
          );
        })}
        {showCreate && (
          <button
            type="button"
            className="tag-picker__chip tag-picker__chip--create"
            onClick={handleCreate}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              add
            </span>
            Créer «&#8239;{trimmedSearch}&#8239;»
          </button>
        )}
        {options.length === 0 && !showCreate && (
          <p className="tag-picker__empty">Aucun tag dans cette catégorie.</p>
        )}
      </div>

      <div className="tag-picker__footer">
        <button type="button" className="tag-picker__btn tag-picker__btn--ghost" onClick={onClose}>
          Annuler
        </button>
        <button
          type="button"
          className="tag-picker__btn tag-picker__btn--primary"
          onClick={handleConfirm}
        >
          Valider ({selectedCount})
        </button>
      </div>
    </div>
  );

  if (popover) return createPortal(body, document.body);

  return createPortal(
    <dialog
      open
      className="tag-picker-overlay"
      aria-modal="true"
      aria-label={tabbed ? 'Tags' : `Tags — ${activeLabel}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      {body}
    </dialog>,
    document.body,
  );
}
